// Pay hub (§3.2, §6): Send money to a beneficiary or Pay via UPI without
// one, plus the recent payments list with receipt access.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChevronRight,
  faClock,
  faQrcode,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { cancelPaymentApproval, getPaymentApproval, listPaymentApprovals, listPayments } from '@/api/payments';
import type { Payment, PaymentApproval } from '@/types/phase2.types';
import { formatIndianCurrency } from '@/utils/currency';
import { RAIL_LABELS } from '@/utils/payments';
import { formatLong } from '@/mock/clock';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import PaymentStatusChip from './components/PaymentStatusChip';
import PaymentReceipt from './components/PaymentReceipt';
import { repeatPath } from './components/payUtils';
import './pay.css';

const RECENT_COUNT = 5;

export const PaymentApprovalDetail = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [approval, setApproval] = useState<PaymentApproval | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setApproval(await getPaymentApproval(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load this approval request.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  const cancel = async () => {
    if (!approval || !approval.canCancel) return;
    setCancelling(true);
    setError('');
    try {
      setApproval(await cancelPaymentApproval(approval.approvalId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to cancel this payment.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="nova-pay-flow"><Loader rows={4} /></div>;
  if (!approval) {
    return <div className="nova-pay-flow"><p className="nova-limit-box">{error || 'Approval request not found.'}</p><Button onClick={() => navigate('/pay')}>Back to payments</Button></div>;
  }

  const statusLabel = approval.status === 'PENDING_APPROVAL' ? 'Awaiting bank approval' : approval.status === 'EXECUTED' ? 'Executed' : approval.status === 'REJECTED' ? 'Rejected' : approval.status === 'CANCELLED' ? 'Cancelled' : 'Failed';
  const statusClass = approval.status === 'REJECTED' || approval.status === 'FAILED' ? 'nova-pay-chip-bad' : approval.status === 'CANCELLED' ? 'nova-pay-chip-cancelled' : approval.status === 'EXECUTED' ? 'nova-pay-chip-ok' : 'nova-pay-chip-warn';

  return (
    <div className="nova-pay-flow nova-approval-detail">
      <div className="nova-pay-flow-header"><button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/pay')}><FontAwesomeIcon icon={faArrowLeft} /></button><h2>Payment approval</h2></div>
      <div className="nova-approval-status"><span className={`nova-pay-chip ${statusClass}`}>{statusLabel}</span><p>{approval.message || (approval.status === 'PENDING_APPROVAL' ? "The amount is on hold until the bank decides. We'll notify you." : '')}</p></div>
      <div className="nova-review-amount"><span className="nova-review-amount-value">{formatIndianCurrency(approval.amountPaise / 100)}</span><span className="nova-review-amount-to">to {approval.beneficiaryName}</span></div>
      <dl className="nova-receipt-rows">
        <div className="nova-receipt-row"><dt>Reference</dt><dd>{approval.reference}</dd></div>
        <div className="nova-receipt-row"><dt>Destination</dt><dd>{approval.beneficiaryAccountNumber}</dd></div>
        <div className="nova-receipt-row"><dt>Mode</dt><dd>{approval.rail}</dd></div>
        <div className="nova-receipt-row"><dt>Approval</dt><dd>{approval.currentLevel} of {approval.levelsRequired} completed</dd></div>
        {approval.rejectionReason && <div className="nova-receipt-row"><dt>Reason</dt><dd>{approval.rejectionReason}</dd></div>}
      </dl>
      {error && <p className="nova-limit-box">{error}</p>}
      {approval.canCancel && <Button variant="outline" onClick={() => void cancel()} isLoading={cancelling}>Cancel payment</Button>}
    </div>
  );
};

export const PaymentApprovals = () => {
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState<PaymentApproval[] | null>(null);

  useEffect(() => {
    listPaymentApprovals().then(setApprovals).catch(() => setApprovals([]));
  }, []);

  return (
    <div className="nova-pay-flow">
      <div className="nova-pay-flow-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/pay')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Payment approvals</h2>
      </div>
      {!approvals && <Loader rows={4} />}
      {approvals?.length === 0 && <p className="nova-pay-empty-line">No payment approval requests.</p>}
      {approvals?.map((approval) => (
        <button type="button" className="nova-pay-payee-row" key={approval.approvalId} onClick={() => navigate(`/pay/approvals/${approval.approvalId}`)}>
          <span className="nova-ben-avatar">{approval.beneficiaryName.charAt(0).toUpperCase()}</span>
          <span className="nova-ben-row-text">
            <span className="nova-ben-alias">{approval.beneficiaryName}</span>
            <span className="nova-ben-count">{approval.reference} · {approval.rail}</span>
          </span>
          <span className="nova-pay-recent-right">
            <span className="nova-pay-recent-amount">{formatIndianCurrency(approval.amountPaise / 100)}</span>
            <span className={`nova-pay-chip ${approval.status === 'REJECTED' || approval.status === 'FAILED' ? 'nova-pay-chip-bad' : approval.status === 'CANCELLED' ? 'nova-pay-chip-cancelled' : approval.status === 'EXECUTED' ? 'nova-pay-chip-ok' : 'nova-pay-chip-warn'}`}>
              {approval.status === 'PENDING_APPROVAL' ? 'Awaiting bank approval' : approval.status === 'EXECUTED' ? 'Executed' : approval.status === 'REJECTED' ? 'Rejected' : approval.status === 'CANCELLED' ? 'Cancelled' : 'Failed'}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
};

const PayHub = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [approvals, setApprovals] = useState<PaymentApproval[] | null>(null);
  const [receiptFor, setReceiptFor] = useState<Payment | null>(null);

  useEffect(() => {
    Promise.all([listPayments(), listPaymentApprovals()])
      .then(([paymentRows, approvalRows]) => { setPayments(paymentRows); setApprovals(approvalRows); })
      .catch(() => { setPayments([]); setApprovals([]); });
  }, []);

  const recent = (payments ?? []).slice(0, RECENT_COUNT);

  return (
    <div className="nova-pay-flow">
      <div className="nova-pay-flow-header">
        <button
          type="button"
          className="nova-icon-btn"
          title="Back"
          aria-label="Back"
          onClick={() => navigate('/dashboard')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Pay someone</h2>
      </div>

      <div className="nova-pay-tiles">
        <button type="button" className="nova-pay-tile" onClick={() => navigate('/pay/send')}>
          <span className="nova-pay-tile-icon">
            <FontAwesomeIcon icon={faUsers} />
          </span>
          <span className="nova-pay-anyupi-text">
            <span className="nova-pay-anyupi-title">Send money</span>
            <span className="nova-pay-anyupi-sub">To your saved beneficiaries</span>
          </span>
          <FontAwesomeIcon icon={faChevronRight} className="nova-pay-chevron" />
        </button>

        <button type="button" className="nova-pay-tile" onClick={() => navigate('/pay/upi')}>
          <span className="nova-pay-tile-icon">
            <FontAwesomeIcon icon={faQrcode} />
          </span>
          <span className="nova-pay-anyupi-text">
            <span className="nova-pay-anyupi-title">Pay via UPI</span>
            <span className="nova-pay-anyupi-sub">Any UPI ID or mobile. No beneficiary needed</span>
          </span>
          <FontAwesomeIcon icon={faChevronRight} className="nova-pay-chevron" />
        </button>

        <button type="button" className="nova-pay-tile" onClick={() => navigate('/pay/approvals')}>
          <span className="nova-pay-tile-icon"><FontAwesomeIcon icon={faClock} /></span>
          <span className="nova-pay-anyupi-text"><span className="nova-pay-anyupi-title">Payment approvals</span><span className="nova-pay-anyupi-sub">{approvals?.filter((item) => item.status === 'PENDING_APPROVAL').length ?? 0} awaiting approval</span></span>
          <FontAwesomeIcon icon={faChevronRight} className="nova-pay-chevron" />
        </button>
      </div>

      <h3 className="nova-pay-section-title">Recent payments</h3>
      {!payments && <Loader rows={3} />}
      {payments && recent.length === 0 && (
        <p className="nova-pay-empty-line">No payments yet. Your recent payments will show here.</p>
      )}
      {recent.map((payment) => (
        <button
          type="button"
          key={payment.id}
          className="nova-pay-payee-row"
          onClick={() => setReceiptFor(payment)}
        >
          <span className="nova-ben-avatar">{payment.payeeName.charAt(0).toUpperCase()}</span>
          <span className="nova-ben-row-text">
            <span className="nova-ben-alias">{payment.payeeName}</span>
            <span className="nova-ben-count">
              {RAIL_LABELS[payment.rail]} · {formatLong(payment.createdAt)}
            </span>
          </span>
          <span className="nova-pay-recent-right">
            <span className="nova-pay-recent-amount">{formatIndianCurrency(payment.amountRupees)}</span>
            <PaymentStatusChip status={payment.status} />
          </span>
        </button>
      ))}

      {receiptFor && (
        <PaymentReceipt
          payment={receiptFor}
          onClose={() => setReceiptFor(null)}
          onRepeat={(p) => navigate(repeatPath(p))}
        />
      )}
    </div>
  );
};

export default PayHub;
